import React, { useState, useEffect } from 'react';
import { ShoppingCart, Users, GraduationCap, Wrench, Settings, MapPin } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import Layout from '../components/layout/Layout';
import { getText, staticTexts } from '../utils/translations';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ServicesPage = () => {
  const { t, language } = useLanguage();
  const [pageImages, setPageImages] = useState({});

  const txt = staticTexts.services;

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/page-images/services`)
      .then(res => res.json())
      .then(data => setPageImages(data || {}))
      .catch(err => console.error('Error fetching page images:', err));
  }, []);

  const serviceIcons = [
    ShoppingCart,
    Users,
    GraduationCap,
    Wrench,
    Settings,
    MapPin
  ];

  return (
    <Layout>
      {/* Page Header */}
      <section className="pt-20 pb-16 bg-slate-100">
        <div className="container mx-auto px-6">
          <p className="text-xs uppercase tracking-widest text-slate-400 mb-4">
            {getText(language, txt.whatWeOffer.en, txt.whatWeOffer.fr, txt.whatWeOffer.ar)}
          </p>
          <h1 className="text-5xl md:text-6xl font-light text-slate-800 mb-6">
            {getText(language, txt.title.en, txt.title.fr, txt.title.ar)}
          </h1>
          <p className="text-slate-500 max-w-2xl">
            {getText(language, txt.subtitle.en, txt.subtitle.fr, txt.subtitle.ar)}
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {t('services.list').map((service, index) => {
              const Icon = serviceIcons[index];
              return (
                <div
                  key={index}
                  className="bg-white rounded-2xl p-8 hover:shadow-lg transition-shadow duration-300"
                >
                  <Icon className="text-slate-400 mb-6" size={28} strokeWidth={1.5} />
                  <h3 className="text-lg font-medium text-slate-800 mb-3">
                    {service.title}
                  </h3>
                  <p className="text-sm text-slate-500 leading-relaxed">
                    {service.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Quality Section */}
      <section className="py-24 bg-slate-100">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <img
                src={pageImages.main?.image_url || "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&q=80"}
                alt="Services"
                className="rounded-2xl w-full"
              />
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-slate-400 mb-4">
                {getText(language, txt.ourCommitment.en, txt.ourCommitment.fr, txt.ourCommitment.ar)}
              </p>
              <h2 className="text-4xl font-light text-slate-800 mb-6">
                {getText(language, txt.quality.en, txt.quality.fr, txt.quality.ar)}<br />
                <span className="font-semibold text-slate-900">
                  {getText(language, txt.excellence.en, txt.excellence.fr, txt.excellence.ar)}
                </span>
              </h2>
              <p className="text-slate-500 leading-relaxed">
                {getText(language, txt.qualityDescription.en, txt.qualityDescription.fr, txt.qualityDescription.ar)}
              </p>
              <div className="mt-8 grid grid-cols-3 gap-6">
                <div>
                  <p className="text-3xl font-light text-slate-800">25+</p>
                  <p className="text-xs text-slate-400 uppercase tracking-wider">
                    {getText(language, txt.years.en, txt.years.fr, txt.years.ar)}
                  </p>
                </div>
                <div>
                  <p className="text-3xl font-light text-slate-800">10+</p>
                  <p className="text-xs text-slate-400 uppercase tracking-wider">
                    {getText(language, txt.partnersCount.en, txt.partnersCount.fr, txt.partnersCount.ar)}
                  </p>
                </div>
                <div>
                  <p className="text-3xl font-light text-slate-800">100%</p>
                  <p className="text-xs text-slate-400 uppercase tracking-wider">
                    {getText(language, txt.coverage.en, txt.coverage.fr, txt.coverage.ar)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ServicesPage;
